import React, { Component } from "react";
import GoogleAnalytics from "react-ga";
import { inject } from 'mobx-react';
import { matchPath } from 'react-router-dom';
import routes from './routes';
import { getPageLabelByPath, getSessionId } from './utils/pageNameMap';
import { saveLastPath } from './utils/lastPath';

GoogleAnalytics.initialize(process.env.REACT_APP_GAID || "G-4DB86XV11N");

// 로컬 개발환경에서는 관리자 사용 분석 데이터 수집 스킵
const isLocalhost = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

const withTracker = (WrappedComponent, options = {}) => {
  const BASENAME = process.env.REACT_APP_BASENAME || "";

  // eslint-disable-next-line
  const HOC = inject('teamUser', 'adminLog', 'common')(
    class extends Component {
      _pageEnterTime = null;
      _prevPathname = null;
      _prevSearch = null;

      sendGA = (page) => {
        if (process.env.NODE_ENV !== "production") return;
        GoogleAnalytics.set({
          page,
          ...options
        });
        GoogleAnalytics.pageview(page);
      };

      // 이전 페이지의 체류 시간을 전송
      sendDuration = () => {
        if (isLocalhost) return;
        if (!this._pageEnterTime || !this._prevPathname) return;
        try {
          const durationMs = Date.now() - this._pageEnterTime;
          // 1초 미만이거나 30분 초과는 무시 (탭 비활성 등 노이즈)
          if (durationMs < 1000 || durationMs > 1800000) return;

          const { teamUser, adminLog, common } = this.props;
          if (!teamUser || !teamUser.isLogin || !teamUser.me || !teamUser.me.emailId) return;

          const matched = routes.find(r =>
            r.path && matchPath(this._prevPathname, { path: r.path, exact: !!r.exact })
          );
          const info = (matched && matched.info) ? matched.info : {};
          if (teamUser.me.emailId === 'admin@onnomads.com' && (info.mainMenu === 'aoa' || info.mainMenu === 'aoaMonitor')) return;

          adminLog.logPageView({
            path: this._prevPathname,
            mainMenu: info.mainMenu || '',
            subMenu: info.subMenu || '',
            pageLabel: getPageLabelByPath(this._prevPathname, common && common.navItems),
            referrer: '',
            query: {},
            type: 'duration',
            durationMs: Math.round(durationMs),
            userAgent: (navigator.userAgent || '').slice(0, 300),
            sessionId: getSessionId(),
            shopNo: teamUser.me.selShopNo || 1
          });
        } catch (e) {
          console.warn('adminLog sendDuration error', e);
        }
      };

      sendAdminLog = (pathname, search) => {
        if (isLocalhost) return;
        try {
          const { teamUser, adminLog, common } = this.props;
          if (!teamUser || !teamUser.isLogin || !teamUser.me || !teamUser.me.emailId) return;

          // routes 에서 현재 path 와 매칭되는 라우트의 info 메타데이터 추출
          const matched = routes.find(r =>
            r.path && matchPath(pathname, { path: r.path, exact: !!r.exact })
          );
          const info = (matched && matched.info) ? matched.info : {};

          // 슈퍼어드민 + aoa(스토어 관리) 메뉴는 프론트에서도 스킵 (백엔드 이중 방어)
          if (teamUser.me.emailId === 'admin@onnomads.com' && (info.mainMenu === 'aoa' || info.mainMenu === 'aoaMonitor')) return;

          // 쿼리 파라미터 → 객체 (서버에서 화이트리스트 적용)
          const queryObj = {};
          if (search) {
            try {
              const usp = new URLSearchParams(search);
              usp.forEach((v, k) => { queryObj[k] = v; });
            } catch (e) {}
          }

          adminLog.logPageView({
            path: pathname,
            mainMenu: info.mainMenu || '',
            subMenu: info.subMenu || '',
            pageLabel: getPageLabelByPath(pathname, common && common.navItems),
            referrer: document.referrer || '',
            query: queryObj,
            userAgent: (navigator.userAgent || '').slice(0, 300),
            sessionId: getSessionId(),
            shopNo: teamUser.me.selShopNo || 1
          });
        } catch (e) {
          // 로그 전송 실패가 라우팅 UX를 망가뜨리면 안 됨
          console.warn('adminLog trackPage error', e);
        }
      };

      componentDidMount() {
        // eslint-disable-next-line
        const pathname = this.props.location.pathname;
        const search = this.props.location.search;
        this.sendGA(`${BASENAME}${pathname}${search}`);
        this.sendAdminLog(pathname, search);
        saveLastPath(pathname);   // 최종 이용 페이지 저장 (로그인 후 복원용)
        this._pageEnterTime = Date.now();
        this._prevPathname = pathname;
        this._prevSearch = search;
      }

      componentDidUpdate(prevProps) {
        const prev = prevProps.location.pathname + prevProps.location.search;
        const next = this.props.location.pathname + this.props.location.search;
        if (prev !== next) {
          // 이전 페이지 체류 시간 전송
          this.sendDuration();
          // 새 페이지 기록
          this.sendGA(`${BASENAME}${next}`);
          this.sendAdminLog(this.props.location.pathname, this.props.location.search);
          saveLastPath(this.props.location.pathname);   // 최종 이용 페이지 저장
          this._pageEnterTime = Date.now();
          this._prevPathname = this.props.location.pathname;
          this._prevSearch = this.props.location.search;
        }
      }

      componentWillUnmount() {
        // 마지막 페이지 체류 시간 전송
        this.sendDuration();
      }

      render() {
        return <WrappedComponent {...this.props} />;
      }
    }
  );

  return HOC;
};

export default withTracker;
